import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import formatBytes from '../utils/formatBytes';

// ---------------------------------------------------------------------------

function FileRow({ file, directory, onDownload, onDelete, downloading }) {
  const filePath = `/${directory}/${file.name}`;
  const isDownloading = downloading === filePath;

  return (
    <ListItem
      secondaryAction={
        <>
          <IconButton
            edge="end"
            aria-label={`download ${file.name}`}
            onClick={() => onDownload(filePath)}
            disabled={isDownloading}
            sx={{ mr: 0.5 }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label={`delete ${file.name}`}
            onClick={() => onDelete(filePath)}
            disabled={isDownloading}
          >
            <DeleteIcon />
          </IconButton>
        </>
      }
    >
      <ListItemText
        primary={file.name}
        secondary={formatBytes(file.size)}
      />
      {isDownloading && (
        <Box sx={{ width: '100%', position: 'absolute', bottom: 0, left: 0 }}>
          <LinearProgress />
        </Box>
      )}
    </ListItem>
  );
}

FileRow.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }).isRequired,
  directory: PropTypes.string.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
};

// ---------------------------------------------------------------------------

/**
 * Renders a single flat list of files for one directory. The parent
 * (FilesTab) owns folder selection and passes in the active directory's files
 * via `files` + `directory`; file paths are built as `/<directory>/<name>`.
 */
export default function DeviceFileBrowser({
  files = [],
  directory = '',
  onDownload,
  onDelete,
  downloading = null,
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDeleteClick = (filePath) => {
    setConfirmDelete(filePath);
  };

  const handleConfirmDelete = () => {
    if (confirmDelete) {
      onDelete(confirmDelete);
    }
    setConfirmDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  if (!files || files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No files found on the device.
      </Typography>
    );
  }

  return (
    <>
      <List dense>
        {files.map((file) => (
          <FileRow
            key={file.name}
            file={file}
            directory={directory}
            onDownload={onDownload}
            onDelete={handleDeleteClick}
            downloading={downloading}
          />
        ))}
      </List>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDelete !== null} onClose={handleCancelDelete}>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{confirmDelete}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DeviceFileBrowser.propTypes = {
  files: PropTypes.array,
  directory: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
};
